import { SERVICE_IDS, OPERATOR_ONLY_SERVICES, type ServiceId } from './service-schema.js';

/**
 * The upper bound on `bookings.passengers_exact` (T651 M7, R21) — and it is a
 * REAL guard rather than tidiness.
 *
 * The column is INTEGER, so a body carrying 1e15 is a perfectly valid safe
 * integer that Postgres refuses with an out-of-range error — and on the quote
 * door that error is raised INSIDE persistQuote's transaction, which would roll
 * the MONEY write back over a dispatch convenience field. The bound has to be
 * below INTEGER's ceiling and it has to be applied before either door opens.
 *
 * 1000 rather than 2147483647: the largest job this operator has ever run is a
 * handful of coaches, so anything above it is a typo or a probe, and a refusal
 * Helen can read beats a five-figure headcount silently persisted onto a
 * dispatch sheet.
 *
 * ⛔ IT LIVES IN src/shared, NOT IN server/app.ts WHERE IT WAS DECLARED (T651
 * M7 webapp-testing, finding 2) — because a client bound the server disagrees
 * with is WORSE than no bound at all: the box would accept a
 * value that is then refused by a 400 the operator may never see, on the door
 * where a refusal costs her the save of a price already agreed on the phone.
 *
 * ⛔ THE BINDERS — the server's readPassengersExact refusal; the Quote screen's
 * `max` attribute plus its inline error (client/components/BandControl.tsx);
 * since T1871 M3 the PUBLIC UNAUTHENTICATED INTAKE (`parsePassengerCount` below,
 * called by server/inquiry.ts; at M4 the site form emits the same number as its
 * own `max`); and since T1871 M7 the CROSSING CARRY in `core/normalise.ts`,
 * which copies an inquiry's count onto the booking and now refuses a value
 * outside `1..MAX_PASSENGERS_EXACT` instead of merely a non-positive one.
 * ⚠️ THIS SENTENCE READ "IT HAS THREE BINDERS NOW" AND LISTED THREE WHILE THAT
 * FOURTH WRITER OF THE COLUMN HAD ALREADY SHIPPED (T1871 M3 added it; M7 gave it
 * the ceiling and this correction). An enumeration reads as complete whether or
 * not it is, so DERIVE IT, never count it from memory:
 * `grep -rln MAX_PASSENGERS_EXACT src` names every module that reaches the
 * constant — more than the doors listed here, because several only cite it in
 * prose — and a module that reaches it and refuses on it while being absent from
 * this list is precisely the defect this paragraph has already shipped once.
 *
 * ⛔⛔ AND ON THE PUBLIC DOOR IT IS THE *SOLE* CONTROL, NOT DEFENCE IN DEPTH.
 * v41 ships `inquiries.passengers_exact` as a plain nullable INTEGER with NO
 * CHECK — deliberately, because a firing CHECK on that table echoes the whole
 * offending row (name, phone, email, both journey ends, three Eircodes) into the
 * error DETAIL and from there into the log, the response and Sentry
 * (core/migrations.ts, the v41 slot, records the ruling and the deliberate
 * asymmetry with v17). So the database will accept a negative, a zero, and
 * anything up to 2^31-1: nothing behind `parsePassengerCount` refuses them.
 * ⚠️ AND BE PRECISE ABOUT THE SCOPE OF THAT CLAIM. Weaken this and there is no
 * DATABASE layer left to catch it — that part is unconditional. IN PROCESS there
 * IS one more reader on the intake path: `engine/bands.ts`'s `bandForCount`
 * re-checks the same three clauses (safe-integer, `>= 1`, `<= MAX`) and returns
 * `null`, which server/inquiry.ts turns into the same 400. MEASURED (T1871 M3,
 * re-measured here rather than taken on trust): deleting `raw >= 1` reds exactly
 * TWO arms — the unit arm in test/shared/passengers.spec.ts and the SCHEMA-level
 * `safeParse` arm in test/server/inquiry.spec.ts, both of which call this
 * function directly. Every HTTP-LEVEL arm stays GREEN, including "refuses zero":
 * a posted `0` still 400s, via `bandForCount` at the handler. ⛔ That agreement
 * is exactly what makes
 * `inquiry.ts`'s `derived === null` branch dead code today, and it is why the
 * two bounds must be changed together or not at all: raise one and the other
 * silently becomes the real boundary.
 * This is the same move, for the same reason, that MAX_WHEELCHAIR_SPACES
 * records in shared/recurrence.ts; that constant's header states the general
 * rule ("a second copy ... is precisely the drift the invoice.ts header
 * refuses"). This file is data-only, so both halves may read it.
 *
 * ⚠️ THE BROWSER ATTRIBUTE IS NOT THE CONTROL. `max` guards nothing that does
 * not come from a browser; the SERVER check is the trust boundary. What the
 * client bound buys is that Helen never produces the value in the first place.
 *
 * ⚠️ 1000 is also MAX_WHEELCHAIR_SPACES' number, deliberately: every wheelchair
 * space is occupied by a passenger. The FLOORS differ (v17 CHECKs `> 0` here, a
 * party of nobody being nonsense; v29 CHECKs `>= 0` there, a run with no
 * wheelchair users being ordinary), which is why they are two constants.
 */
export const MAX_PASSENGERS_EXACT = 1000;

/**
 * ⭐ T1871 M3 — THE ONE PARSE FOR A SUBMITTED HEADCOUNT, and the trust boundary
 * of the public unauthenticated intake. Returns the count, or `null` for
 * everything that is not one. It never throws and it never coerces.
 *
 * ⛔ NOTHING ON THIS PATH MAY USE `Number()`, `+x`, an unguarded `parseInt`, OR
 * `z.coerce.number()`. `z.coerce.number()` is LITERALLY `Number(input)`, so
 * `''`, `' '`, `null`, `false` and `[]` all become `0` and then satisfy
 * `.int()` — a party of nobody, accepted, on a public form. `parseInt` is worse:
 * it reads `'7 people'` as 7 and `'0x10'` as 16.
 *
 * ⚠️ `Number.isSafeInteger`, NOT `Number.isInteger` — AND THE DIFFERENCE IS
 * CURRENTLY UNOBSERVABLE HERE, WHICH IS WHY IT IS WRITTEN DOWN RATHER THAN
 * TRUSTED TO A TEST. The two predicates disagree only ABOVE 2^53, and every such
 * value is already past `MAX_PASSENGERS_EXACT` (1000), so the ceiling refuses
 * `1e21` and `MAX_SAFE_INTEGER + 1` first. MEASURED BY MUTATION, T1871 M3:
 * swapping this clause to `Number.isInteger` leaves every BEHAVIOURAL arm green;
 * the ONLY thing that reds is the labelled TEXT PIN in
 * test/shared/passengers.spec.ts. ⛔ THE DISTINCTION IS THE WHOLE POINT — say
 * "the suite stays green" and the next editor weakens the clause, sees one red,
 * reads this docblock, concludes the red is spurious, and deletes the only guard
 * the clause has.
 * It stays `isSafeInteger` because it is the clause that keeps the promise
 * INDEPENDENTLY OF THE CEILING: `1e21` is an integer by the weaker predicate,
 * node-postgres serialises it as the text `"1e+21"`, and PostgreSQL refuses it
 * with a bigint syntax error — an exception inside the insert,
 * `captureException`, a 500, and a LOST LEAD where a 400 was the promise. Raise
 * `MAX_PASSENGERS_EXACT` past 2^53 and this clause becomes the only thing
 * standing between that body and a 500. The behavioural arms cannot hold it, so
 * a TEXT PIN in test/shared/passengers.spec.ts does, and says so.
 * It also refuses `NaN`, `Infinity` and every fractional value — those ARE
 * behaviourally graded.
 *
 * The shape deliberately mirrors the kiosk door's shipped `positiveIntField`
 * (server/app.ts): a JSON number, safe-integer, `>= 1`, `<= MAX_PASSENGERS_EXACT`.
 * One shape, two doors; see this constant's own header for why there is no
 * second ceiling.
 *
 * ⚠️ IT IS TOTAL OVER `unknown` BY CONSTRUCTION — the first clause is a
 * `typeof` test, so every hostile shape (a string, an array, an object, a
 * `Symbol`, a getter-bearing proxy) leaves through the same `null` without any
 * property of the input being read. That totality is what lets it sit inside a
 * zod predicate, where a refinement is still invoked on data an earlier check
 * has already marked dirty.
 */
export function parsePassengerCount(raw: unknown): number | null {
  return typeof raw === 'number' &&
    Number.isSafeInteger(raw) &&
    raw >= 1 &&
    raw <= MAX_PASSENGERS_EXACT
    ? raw
    : null;
}

/**
 * ⭐⭐ T1871 M6 — THE ONE RENDER OF A CAPTURED HEADCOUNT, and the ONE place the
 * NULL rule is written as code rather than remembered at five call sites.
 *
 * Returns `'20 passengers'` / `'1 passenger'`, or **null** for every value that
 * is not a captured count. Every surface that shows the number — the Bookings
 * list, the booking detail, the kiosk journey summary, the driver message and
 * the customer quote — calls this and renders NOTHING when it answers null.
 *
 * ⛔⛔ NULL MEANS "NOT CAPTURED", NEVER "A PARTY OF NOBODY" AND NEVER A LESSER
 * BOOKING. `bookings.passengers_exact` is NULL on every pre-v17 row, on every
 * row M3's legacy transition arm accepts, and on every nursing-home crossing
 * (which collects no count at all, by decision) — so absence is the MAJORITY
 * answer and must read as quiet, not as degraded. Nothing may gate a screen, a
 * transition or an action on this being non-null, and nothing may derive a
 * confidence or completeness flag from it.
 *
 * ⚠️ IT DELEGATES TO `parsePassengerCount` RATHER THAN RE-TESTING, which is what
 * keeps `Number(null) === 0` and `Number('') === 0` out of every render: that
 * function is a `typeof`-first total predicate over `unknown` that never
 * coerces, so `null`, `''`, `0`, `'20'`, `NaN` and `20.5` all leave through the
 * same null and no surface can print a fabricated headcount.
 *
 * ⚠️ AND IT REFUSES A COUNT ABOVE `MAX_PASSENGERS_EXACT`, deliberately. Every
 * write door bounds at that ceiling, so such a row cannot be produced today; if
 * one ever exists (a hand-run UPDATE), showing nothing and letting the band
 * speak is the honest direction — the alternative is a dispatch sheet asserting
 * a figure the system itself would refuse to accept.
 *
 * ⚠️ THE PLURAL IS COMPOSED HERE, not at the call sites, for the reason the
 * add-on `customerLine` docblock states about its own singular stem: a plural
 * rule copied to five surfaces is a plural rule that will disagree on one of
 * them. "passenger" is regular, so the `s` is the whole rule.
 */
export function formatPassengerCount(raw: unknown): string | null {
  const n = parsePassengerCount(raw);
  return n === null ? null : `${n} passenger${n === 1 ? '' : 's'}`;
}

/**
 * booking_type is a Postgres ENUM (migrations.ts) of the persisted services +
 * the Phase-2 contract seed. Operator-only POC services (nursing-home) are NOT
 * enum members and are NOT persisted with their own type: a nursing-home job
 * stores as 'local-other' (serviceToBookingType maps it below), while the kiosk
 * keeps the nursing-home identity in client state and builds the invoice there.
 * So BookingType deliberately EXCLUDES operator-only ids to match the DB enum.
 */
export type PersistedServiceId = Exclude<ServiceId, (typeof OPERATOR_ONLY_SERVICES)[number]>;
export type BookingType = PersistedServiceId | 'recurring_contract';

const BOOKING_TYPES = new Set<BookingType>([
  ...SERVICE_IDS.filter(
    (id): id is PersistedServiceId => !(OPERATOR_ONLY_SERVICES as readonly string[]).includes(id),
  ),
  'recurring_contract',
]);

/** Representative passenger count for a capacity band, e.g. "8-12" -> 12, "16+" -> 16. */
export function bandToPax(band: string): number {
  const nums = band.match(/\d+/g);
  if (!nums || nums.length === 0) {
    throw new Error(`bandToPax: cannot parse band "${band}"`);
  }
  return Number(nums[nums.length - 1]);
}

/** Map an inquiry/service id onto the booking_type enum; unknown -> 'local-other'. */
export function serviceToBookingType(service: string): BookingType {
  return BOOKING_TYPES.has(service as BookingType)
    ? (service as BookingType)
    : 'local-other';
}
